import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Pricing.css";
import Slider from "./Slider.js";
import GoogleReviewSection from "../Home/GoogleReviewSection.js";
import ContactForm from "../ContactForm/ContactFormModal.js";

const PricingSlider = () => {
  const [sliderValue, setSliderValue] = useState(1);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [] = useState(null);

  const maxDots = 6;
  const baseBookkeepingPrice = 100;
  const baseCFOPrice = 200;
  const priceIncrement = 60;

  const monthlyExpense = baseBookkeepingPrice + (sliderValue - 1) * priceIncrement;
  const monthlyExpenseRange = 20000 + (sliderValue - 1) * 15000;

  const handleSliderChange = (event) => {
    setSliderValue(parseInt(event.target.value, 10));
  };

  const expenseRangeStyles = {
    fontSize: '25px',
    color: 'green',
    fontFamily: 'Arial, sans-serif',
  };

  const handleGetStartedClick = (planTitle) => {
    // Redirect to login page with the selected plan as state
    window.location.href = `${process.env.REACT_APP_REDIRECT_URL}/login?redirectedFrom=pricing&plan=${encodeURIComponent(planTitle)}`;
  };

  const handleCloseContactForm = () => {
    setIsContactFormOpen(false);
  };

  const plans = [
    {
      title: "USA Company Formation",
      price: "$200",
      frequency: "One Time",
      features: [
        "Formation Document",
        "Registered Agent Services (1 year)",
        "EIN and ITIN (Individual Tax Identification Number)",
        "Foreign Qualification",
      ],
      button: "Buy Now",
      color: "#f0f7ff",
      note: "(State Taxes extra)"
    },
    {
      title: "Tax and Form Return",
      price: "$150",
      frequency: "Annual Basis",
      features: [
        "Individual Tax Return (Form 1040 and 1040NR)",
        "Business Tax Return (Form 1065, 1120 and 1120S)",
        "USA State Annual Report Filing ",
      ],
      button: "Buy Now",
      color: "#e0f4ff",
    },
    {
      title: "Pay-As-You-Go",
      price: "$8",
      frequency: "per hour",
      features: [
        "Dedicated Accountant",
        "Financial Reports",
        "Cash Basis or Accrual Basis",
        "Bank and Credit Card Reconciliation",
        "Books Clean-up"
      ],
      button: "Buy Now",
      color: "#fff7f0",
    },
    {
      title: "Bookkeeping",
      price: `$${monthlyExpense}`,
      frequency: "per month",
      features: [
        "Dedicated Accountant",
        "Monthly or Weekly Accounting",
        "Cash Basis or Accrual Basis",
        "Monthly Financial Reports",
      ],
      button: "Buy Now",
      color: "#f5f0ff",
    },
    {
      title: "CFO Advisory",
      price: `$${baseCFOPrice + (sliderValue - 1) * priceIncrement}`,
      frequency: "per month",
      features: [
        "Dedicated CFO Analyst",
        "Monthly and Quarterly CFO Meeting",
        "Forecasting and Budgeting",
        "Monthly Financial Reports",
        "Cash Flow Management",
        "Strategic Planning"
      ],
      button: "Buy Now",
      color: "#f0e5ff",
    },
    {
      title: "Registered Agent",
      price: "$200",
      frequency: "per year",
      features: [
        'Annual Registered Agent Service',
        'Mail forwarding and handling',
        'Compliance notifications',
        'Document management',
        'Legal address maintenance'
      ],
      button: "Buy Now",
      color: "#f0e5ff",
    },
  ];

  return (
    <section className="pricing-slider-section">
      <div className="container text-center">
        <h2 className="section-title2">Pricing Plans</h2>
        <p className="section-subtitle">
          Tailored Solutions for Every Stage of Growth
        </p>

        {/* First Row: 3 Fixed Pricing Cards */}
        <div className="row justify-content-center">
          {plans.slice(0, 3).map((plan, index) => (
            <div key={index} className="col-12 col-md-4 mb-4">
              <div className="pricing-card" style={{ backgroundColor: plan.color }}>
                <h3 className="plan-title">{plan.title}</h3>
                <h4 className="plan-price">
                  <small className="starting-at" style={{
                    fontSize: '0.8rem',
                    display: 'block',
                    marginRight: '60px'
                  }}>Starting at</small>
                  <span className="dollar-sign">{plan.price}</span>
                  <span className="frequency"> {plan.frequency}</span>
                  {plan.note && <small className="tax-note" style={{
                    display: 'block',
                    fontSize: '1rem',
                    color: '#666',
                    marginTop: '5px',
                    fontStyle: 'italic'
                  }}>{plan.note}</small>}
                </h4>
                <ul className="plan-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
                <button
                  className="btn btn-primary"
                  onClick={() => handleGetStartedClick(plan.title)}
                >
                  {plan.button}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="position-relative mt-3">
          <input
            type="range"
            className="form-range"
            min="1"
            max={maxDots}
            step="1"
            value={sliderValue}
            onChange={handleSliderChange}
          />

          {/* Dotted Track */}
          <div className="slider-dots">
            {Array.from({ length: maxDots }, (_, index) => (
              <div
                key={index}
                className={`dot ${index < sliderValue ? "active" : ""}`}
              ></div>
            ))}
          </div>

          {/* Expense Range Indicator */}
          <div className="expense-range-indicator mt-2" style={expenseRangeStyles}>
            Up to <b>${monthlyExpenseRange.toLocaleString()}</b> in monthly expenses
          </div> <br></br>
        </div>

        {/* Second Row: 2 Cards with Dynamic Pricing */}
        <div className="row justify-content-center">
          {plans.slice(3, 6).map((plan, index) => (
            <div key={index} className="col-12 col-md-4 mb-4">
              <div className="pricing-card" style={{ backgroundColor: plan.color }}>
                <h3 className="plan-title">{plan.title}</h3>
                <h4 className="plan-price">
                  <small className="starting-at">Starting at</small>
                  <span className="dollar-sign">{plan.price}</span>
                  <span className="frequency"> {plan.frequency}</span>
                </h4>
                <ul className="plan-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
                <button
                  className="btn btn-primary"
                  onClick={() => handleGetStartedClick(plan.title)}
                >
                  {plan.button}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

       {/* Contact Modal */}
      {isContactFormOpen && <ContactForm onClose={handleCloseContactForm} />}
      <div className="mt-5">
        <GoogleReviewSection />
      </div>
      <Slider />
    </section>
  );
};

export default PricingSlider;